// ============================================================================
// [CORRIGIDO] PÁGINA DE VISÃO GERAL - LÓGICA DE VISUALIZAÇÃO DE RESULTADOS
// ============================================================================
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import EvaluationResultModal from '../components/EvaluationResultModal';

// Importando o Hook do contexto de autenticação
import { useAuth } from '../context/AuthContext';

// Imports do Material UI
import {
    Container, 
    Box, 
    Typography, 
    Grid, 
    Paper, 
    Card, 
    CardContent, 
    Chip, 
    LinearProgress, 
    Divider, 
    CardActions, 
    Button, 
    Dialog, 
    DialogTitle,
    DialogContent, 
    List, 
    ListItem, 
    ListItemIcon, 
    ListItemText,
    CircularProgress, 
    TextField, 
    DialogActions,
    IconButton
} from '@mui/material';

import {
    Visibility, 
    NotificationsActive,
    CheckCircle as CheckCircleIcon, 
    Cancel as CancelIcon, 
    AccountTree as HierarchyIcon,
    ErrorOutline
} from '@mui/icons-material';

const AdminOverviewPage = () => {
    const { token, user } = useAuth();
    const navigate = useNavigate();
    
    // --- ESTADOS ---
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState([]);
    const [units, setUnits] = useState([]);
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [dicionarioCompetencias, setDicionarioCompetencias] = useState({ corporativas: [], gerenciais: [] });

    // --- LÓGICA DE PERMISSÕES ---
    const isAdminOrEditor = user && ['superadmin', 'admin', 'editor'].includes(user.role);
    
    const isManagerOnly = useMemo(() => {
        if (isAdminOrEditor) return false;
        const searchStr = 'vaga gerencial';
        const check = (field) => field && field.toString().toLowerCase().includes(searchStr);
        return user?.isGerencial || check(user?.jobDescription) || check(user?.cargo);
    }, [user, isAdminOrEditor]);

    // Estados dos Modais
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [notifyModalOpen, setNotifyModalOpen] = useState(false);
    const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
    const [selectedEvaluationNode, setSelectedEvaluationNode] = useState(null);
    const [selectedAreaDetails, setSelectedAreaDetails] = useState(null);
    const [notificationData, setNotificationData] = useState({ areaName: '', message: '' });

    // Função recursiva para achar subordinados
    const findAllSubordinateIds = useCallback((startNodeId, allNodes) => {
        const subordinates = new Set();
        const queue = [startNodeId.toString()];
        
        while (queue.length > 0) {
            const currentId = queue.shift();
            const children = allNodes.filter(n => n.parent_id?.toString() === currentId);
            
            children.forEach(child => {
                if (!subordinates.has(child.id.toString())) {
                    subordinates.add(child.id.toString());
                    queue.push(child.id.toString());
                }
            });
        }
        return subordinates;
    }, []);

    // --- 1. BUSCA INICIAL ---
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [unitsRes, competenciesRes] = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_URL}/units/stats`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }),
                    fetch(`${process.env.REACT_APP_API_URL}/company/competencies`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                ]);

                const unitsData = await unitsRes.json();
                const competenciesData = await competenciesRes.json();

                setUnits(unitsData || []);
                setDicionarioCompetencias(competenciesData || { corporativas: [], gerenciais: [] });
                
                const userUnitId = user?.unitid || user?.unit_id;
                if (userUnitId) {
                    setSelectedUnitId(userUnitId.toString());
                } else if (unitsData && unitsData.length > 0) {
                    setSelectedUnitId(unitsData[0].id.toString());
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error("Erro na carga inicial:", error);
                setLoading(false);
            }
        };
        if (token) fetchInitialData();
    }, [token, user]);

    // --- 2. CÁLCULO DE ESTATÍSTICAS (AQUI ESTÁ A CORREÇÃO LÓGICA) ---
    useEffect(() => {
        const calculateStats = async () => {
            if (!selectedUnitId) return;
            
            setLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/organograma/unit/${selectedUnitId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const allNodes = await response.json();

                if (!allNodes || !Array.isArray(allNodes)) {
                    setStats([]);
                    return;
                }

                let filteredNodes = [];

                if (isAdminOrEditor) {
                    filteredNodes = allNodes;
                } else {
                    const myNode = allNodes.find(n => n.user_id?.toString() === user?.id?.toString());
                    
                    if (myNode) {
                        const subordinateIds = findAllSubordinateIds(myNode.id, allNodes);
                        filteredNodes = allNodes.filter(n => subordinateIds.has(n.id.toString()));
                    } else {
                        const managerAreaId = user?.areaid || user?.area_id;
                        filteredNodes = allNodes.filter(n => 
                            n.area_id?.toString() === managerAreaId?.toString() && 
                            n.user_id?.toString() !== user?.id?.toString()
                        );
                    }
                }

                const areaMap = {};

                filteredNodes.forEach(node => {
                    if (!node.cargo) return;

                    const areaName = node.areaName || 'Área Não Definida';

                    if (!areaMap[areaName]) {
                        areaMap[areaName] = {
                            name: areaName,
                            totalCards: 0,
                            completedEvaluations: 0,
                            color: node.color || '#94a3b8',
                            employees: []
                        };
                    }

                    areaMap[areaName].totalCards++;

                    // Verifica se tem histórico
                    const history = node.evaluationHistory || [];
                    const hasFinishedCycle = Array.isArray(history) && history.length > 0;
                    
                    if (hasFinishedCycle) {
                        areaMap[areaName].completedEvaluations++;
                    }
                    
                    // --- ✨ CORREÇÃO CRÍTICA AQUI ✨ ---
                    // Se tiver histórico, usamos a avaliação mais recente do histórico para exibir nos gráficos.
                    // Se não tiver, usamos o rascunho (evaluations) que pode estar vazio.
                    let evaluationsForDisplay = node.evaluations || {};

                    if (hasFinishedCycle) {
                        // Ordena para pegar o mais recente
                        const sortedHistory = [...history].sort((a, b) => new Date(b.date) - new Date(a.date));
                        if (sortedHistory[0] && sortedHistory[0].evaluations) {
                            evaluationsForDisplay = sortedHistory[0].evaluations;
                        }
                    }
                    // ------------------------------------

                    const techCompArray = node.competenciasTecnicas || [];
                    const techCount = Array.isArray(techCompArray) ? techCompArray.length : 0;
                    const canEvaluate = techCount >= 3;

                    areaMap[areaName].employees.push({
                        id: node.id,
                        name: node.nome || 'Vaga em Aberto',
                        role: node.cargo,
                        status: hasFinishedCycle ? 'Concluído' : 'Pendente',
                        isVacant: !node.user_id,
                        canEvaluate: canEvaluate,
                        nodeData: {
                            ...node,
                            data: {
                                name: node.nome || 'Vaga',
                                // ✨ Injeta a avaliação correta (Histórico ou Rascunho)
                                evaluations: evaluationsForDisplay, 
                                competenciasTecnicas: node.competenciasTecnicas || [],
                                outrasCompetencias: node.outrasCompetencias || [],
                                isGerencial: node.isGerencial,
                                evaluationHistory: history,
                                areaName: node.areaName
                            }
                        }
                    });
                });

                const statsArray = Object.values(areaMap).map(area => ({
                    ...area,
                    percentage: area.totalCards > 0 
                        ? Math.round((area.completedEvaluations / area.totalCards) * 100) 
                        : 0
                }));

                statsArray.sort((a, b) => a.percentage - b.percentage);
                setStats(statsArray);

            } catch (error) {
                console.error("Erro no processamento:", error);
            } finally {
                setLoading(false);
            }
        };

        calculateStats();
    }, [selectedUnitId, token, user, isManagerOnly, isAdminOrEditor, findAllSubordinateIds]);

    // --- HANDLERS ---
    const handleOpenDetails = (area) => {
        setSelectedAreaDetails(area);
        setDetailsModalOpen(true);
    };
    
    const handleOpenEvaluationModal = (nodeData) => {
        setSelectedEvaluationNode(nodeData);
        setIsEvaluationModalOpen(true);
    };

    const handleOpenNotify = (areaName) => {
        setNotificationData({
            areaName: areaName,
            message: `Olá Equipe de ${areaName},\n\nIdentificamos que há avaliações de desempenho pendentes. Favor regularizar.`
        });
        setNotifyModalOpen(true);
    };

    const handleSendNotification = () => {
        alert("Notificação enviada!");
        setNotifyModalOpen(false);
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        {isManagerOnly ? 'Minha Equipe' : 'Visão Geral de Avaliações'}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b' }}>
                        {isManagerOnly 
                            ? `Acompanhe e realize as avaliações dos seus colaboradores.` 
                            : 'Monitoramento global do progresso de avaliações.'}
                    </Typography>
                </Box>

                {isAdminOrEditor && units.length > 0 && (
                    <select 
                        value={selectedUnitId}
                        onChange={(e) => setSelectedUnitId(e.target.value)}
                        style={{ padding: '10px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', fontWeight: '600' }}
                    >
                        {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                )}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
            ) : (
                <Grid container spacing={3}>
                    {stats.length === 0 ? (
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 10, textAlign: 'center', borderRadius: '20px', borderStyle: 'dashed' }}>
                                <HierarchyIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    Não encontramos subordinados vinculados ao seu usuário.
                                </Typography>
                            </Paper>
                        </Grid>
                    ) : (
                        stats.map((area) => (
                            <Grid item xs={12} sm={6} md={4} key={area.name}>
                                <Card elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 3 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                                {area.name}
                                            </Typography>
                                            <Chip label={`${area.percentage}%`} color={area.percentage === 100 ? "success" : "warning"} sx={{ fontWeight: 900 }} />
                                        </Box>

                                        <LinearProgress variant="determinate" value={area.percentage} sx={{ height: 8, borderRadius: 5, mb: 2 }} />
                                        
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            {area.completedEvaluations} de {area.totalCards} colaboradores avaliados
                                        </Typography>
                                    </CardContent>
                                    <Divider />
                                    <CardActions sx={{ p: 2, bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
                                        <Button startIcon={<Visibility />} onClick={() => handleOpenDetails(area)} sx={{ fontWeight: 700 }}>Gerenciar Área</Button>
                                        {isAdminOrEditor && (
                                            <IconButton onClick={() => handleOpenNotify(area.name)}>
                                                <NotificationsActive color="warning" />
                                            </IconButton>
                                        )}
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>
            )}

            {/* Modal de Detalhes da Equipe */}
            <Dialog 
                open={detailsModalOpen} 
                onClose={() => setDetailsModalOpen(false)} 
                maxWidth="sm" 
                fullWidth 
                PaperProps={{ sx: { borderRadius: '20px' } }}
            >
                <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid #e2e8f0' }}>
                    Equipe: {selectedAreaDetails?.name}
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <List>
                        {selectedAreaDetails?.employees.map((emp, idx) => (
                            <React.Fragment key={emp.id}>
                                <ListItem
                                    sx={{ py: 2, px: 3 }}
                                    secondaryAction={
                                        emp.status === 'Concluído' ? (
                                            <Button variant="outlined" color="success" size="small" onClick={() => handleOpenEvaluationModal(emp.nodeData)} sx={{ fontWeight: 700 }}>Resultado</Button>
                                        ) : (
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Button 
                                                    variant="contained" 
                                                    color={emp.canEvaluate ? "warning" : "inherit"} 
                                                    size="small" 
                                                    disabled={!emp.canEvaluate}
                                                    onClick={() => navigate(`/organograma?nodeId=${emp.id}&openEvaluation=true`)}
                                                    sx={{ 
                                                        fontWeight: 700,
                                                        backgroundColor: emp.canEvaluate ? '#f59e0b' : '#e2e8f0'
                                                    }}
                                                >
                                                    Avaliar
                                                </Button>
                                                {!emp.canEvaluate && (
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            display: 'block', 
                                                            color: 'error.main', 
                                                            fontSize: '0.65rem', 
                                                            mt: 0.5,
                                                            fontWeight: 600,
                                                            maxWidth: '120px',
                                                            lineHeight: 1.1
                                                        }}
                                                    >
                                                        Defina no mínimo 3 competências técnicas
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }
                                >
                                    <ListItemIcon>
                                        {emp.status === 'Concluído' 
                                            ? <CheckCircleIcon color="success" /> 
                                            : (emp.canEvaluate ? <CancelIcon color="disabled" /> : <ErrorOutline color="error" />)
                                        }
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={<Typography sx={{ fontWeight: 700, color: emp.canEvaluate ? 'inherit' : '#94a3b8' }}>{emp.name}</Typography>} 
                                        secondary={emp.role} 
                                    />
                                </ListItem>
                                {idx < selectedAreaDetails.employees.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setDetailsModalOpen(false)} sx={{ fontWeight: 700 }}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <EvaluationResultModal 
                isOpen={isEvaluationModalOpen} 
                onClose={() => setIsEvaluationModalOpen(false)} 
                node={selectedEvaluationNode} 
                dicionarioCompetencias={dicionarioCompetencias} 
            />

            {/* Modal de Notificação Simples */}
            <Dialog open={notifyModalOpen} onClose={() => setNotifyModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Notificar Liderança</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={notificationData.message}
                        onChange={(e) => setNotificationData({ ...notificationData, message: e.target.value })}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setNotifyModalOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSendNotification} variant="contained" sx={{ backgroundColor: '#1e293b' }}>Enviar</Button>
                </DialogActions>
            </Dialog>

        </Container>
    );
};

export default AdminOverviewPage;